import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useSWR, { mutate } from "swr";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../supabaseClient";

type CandidateFormProps = {
  onSuccess: () => void;
  initialData?: any | null;
};

type CandidateData = {
  name: string;
  email: string;
  phone: string;
  location: string;
  skills: string;
  experience: string;
  source: string;
  status: string;
  linkedInUrl: string;
  notice_period: string;
  current_ctc: string,
  expected_ctc: string,
};

export default function CandidateForm({
  onSuccess,
  initialData,
}: CandidateFormProps) {
  const { toast } = useToast();

  const form = useForm<CandidateData>({
    defaultValues: initialData || {
      name: "",
      email: "",
      phone: "",
      location: "",
      skills: "",
      experience: "",
      source: "internal",
      status: "active",
      linkedInUrl: "",
      current_ctc: "",
      expected_ctc: "",
    },
  });

  const onSubmit = async (values: CandidateData) => {
    try {
      const processedValues = {
        ...values,
        skills: values.skills
          .split(",") 
          .map((skill) => skill.trim()) 
          .filter((skill) => skill !== ""), 
      };
  
      const { data, error } = initialData
        ? await supabase
            .from("candidates")
            .update(processedValues)
            .eq("id", initialData.id)
        : await supabase.from("candidates").insert(processedValues);
  
      if (error) {
        throw new Error(error.message);
      }
  
      toast({
        title: "Success",
        description: initialData
          ? "Candidate updated successfully"
          : "Candidate created successfully",
      });
  
     
      mutate("/api/candidates");
  
     
      onSuccess();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  required
                  minLength={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  {...field}
                  required
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  {...field}
                  pattern="[0-9]{10}"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <Input
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="experience"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Years of Experience</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value ? parseInt(value, 10) : "");
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

      <FormField
        control={form.control}
        name="skills"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Skills</FormLabel>
            <FormControl>
              <Input
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        />

    <FormField
        control={form.control}
        name="notice_period"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notice Period</FormLabel>
            <FormControl>
              <Input
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        />
         <FormField
        control={form.control}
        name="current_ctc"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Current CTC</FormLabel>
            <FormControl>
              <Input
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        /> <FormField
        control={form.control}
        name="expected_ctc"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Expected CTC</FormLabel>
            <FormControl>
              <Input
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        />


        <FormField
          control={form.control}
          name="source"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Source</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="linkedInUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LinkedIn URL</FormLabel>
              <FormControl>
                <Input
                  type="url"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          {initialData ? "Update Candidate" : "Create Candidate"}
        </Button>
      </form>
    </Form>
  );
}
